/**
 * webPush — browser Push API subscription helpers (web/desktop only).
 *
 * Native iOS/Android use APNs/FCM via expo-notifications and never touch this
 * file. On web/electron we drive a separate flow:
 *
 *   1. Service worker (`mobile/web/service-worker.js`) registered at app start
 *      from `index.html`. It handles `push`, `notificationclick`, and
 *      `pushsubscriptionchange` events, and forwards click data to the React
 *      tree via `postMessage` (DesktopLayout listens).
 *   2. After auth, `subscribeWebPush()` requests permission, asks the SW for
 *      a `PushSubscription` against the VAPID public key, and POSTs it to
 *      `/users/create-push-subscription/{account_id}/{cognito_id}`. The same
 *      backend endpoint the legacy web app already uses — backend code is
 *      unchanged.
 *   3. On sign-out (or explicit revoke from settings), `unsubscribeWebPush()`
 *      tears down the browser subscription and POSTs an `inactive` status to
 *      the backend's update-push-subscription endpoint.
 *
 * All entry points are no-ops outside web/electron and degrade gracefully
 * when the browser doesn't expose `serviceWorker` / `PushManager`.
 */

import config from '../config';
import { apiPost } from '../utilities/apiClient';
import { isWeb } from '../utilities/platform';
import { store } from '../store';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PushSubscriptionConfig {
  // JSON-stringified before send (column on push_subscriptions is JSONB
  // and the backend lambda spreads ...config into a raw insert that
  // doesn't auto-stringify). Type is `string` on the wire, the DB stores
  // it as the parsed JSON object.
  device?: string;
  device_id?: string;
  time_zone?: string;
}

interface SubscribeResult {
  success: boolean;
  message?: string;
  /** True when the browser/runtime can't host a push subscription (no SW, etc.) */
  unsupported?: boolean;
  /** True when permission was actively denied by the user. */
  denied?: boolean;
  /**
   * True when nothing changed — both the browser and backend already had this
   * endpoint on file before the call. Lets the UI distinguish "newly enabled"
   * from "already enabled" so a toast/alert can reflect reality instead of
   * implying a fresh subscription was created.
   */
  alreadyActive?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const isWebRuntime = (): boolean =>
  isWeb && typeof navigator !== 'undefined' && typeof window !== 'undefined';

const hasPushSupport = (): boolean =>
  isWebRuntime()
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && typeof Notification !== 'undefined';

/**
 * Convert a base64url-encoded VAPID key to the Uint8Array format the Push API
 * expects for `applicationServerKey`. Mirrors the legacy web app's helper.
 */
function vapidKeyToUint8Array(base64Url: string): Uint8Array | null {
  try {
    const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
    const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.error('[webPush] failed to decode VAPID key', err);
    }
    return null;
  }
}

/**
 * Wait for the registered service worker to reach the `activated` state.
 * `navigator.serviceWorker.ready` resolves when there's an active SW for the
 * scope.
 *
 * The timeout here is ONLY a backstop for a genuinely stuck registration — it
 * must NOT clip a normal cold activation. The previous 2s cap did exactly that:
 * on a cold load the SW often isn't activated within 2s, so getSubscription()
 * returned null and the settings toggle false-negatived to OFF even though the
 * browser was subscribed. That single unreliable read is what every prior
 * "source of truth" patch was really fighting. We fast-fail when no SW is
 * registered at all (so dev/Expo runs don't hang), which means whenever a
 * registration DOES exist, `ready` will resolve once it activates — a generous
 * backstop just guards against a wedged install.
 *
 * Returns `null` when no SW is registered for this origin (Expo's
 * `expo start --web` dev server doesn't serve our SW; only the Electron/webpack
 * build at `desktop/webpack.config.js` and `mobile/web/index.html` does), or if
 * an existing registration never activates within the backstop window.
 */
async function awaitActiveServiceWorker(timeoutMs = 10000): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    // Fast-fail when nothing is registered — `serviceWorker.ready` would
    // hang forever (it never rejects on its own) if no registration exists,
    // so we'd otherwise eat the full timeout on every dev-server load.
    const registrations = await navigator.serviceWorker.getRegistrations();
    if (registrations.length === 0) return null;
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<ServiceWorkerRegistration>((_, rej) =>
        setTimeout(() => rej(new Error('service-worker-timeout')), timeoutMs)
      ),
    ]);
  } catch {
    return null;
  }
}

/**
 * True when the page has no service worker registration at all. Used to
 * distinguish "push genuinely unavailable on this surface" from "push
 * temporarily failed" so the bootstrap caller doesn't yell about Expo
 * dev-server runs that never register a SW in the first place.
 */
async function hasAnyServiceWorkerRegistration(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    return regs.length > 0;
  } catch {
    return false;
  }
}

/** Detect a small descriptor for the device — best-effort, never throws. */
function describeDevice(): Record<string, unknown> {
  if (!isWebRuntime()) return {};
  const ua = navigator.userAgent || '';
  const platform = (navigator as any).userAgentData?.platform || (navigator as any).platform || '';
  const language = navigator.language || '';
  return { ua, platform, language, deviceType: 'web' };
}

function readTimezone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { return ''; }
}

/**
 * Persist a per-install device id under localStorage so the backend can
 * distinguish multiple devices for the same Cognito user. The mobile redux
 * store doesn't model `device_id` yet; using localStorage keeps this isolated
 * and doesn't drag the legacy web's deviceId reducer over.
 */
function getOrCreateDeviceId(): string {
  if (!isWebRuntime() || typeof localStorage === 'undefined') return '';
  const KEY = 'eleplan.webPush.deviceId';
  try {
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = (typeof crypto !== 'undefined' && (crypto as any).randomUUID)
        ? (crypto as any).randomUUID()
        : 'web-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    return '';
  }
}

/**
 * Pull `account_id` and `cognito_id` out of the redux store. Returns null when
 * the user isn't authenticated yet — caller should treat this as "skip for now,
 * try again next time auth state changes."
 */
function getAuthIdentity(): { account_id: string; cognito_id: string } | null {
  const state: any = store.getState();
  const account_id = state?.session?.account?.account_id;
  // ED-2334. The authenticated user's cognito_id, not the account
  // owner's. For partner-invited members, `session.account.cognito_id`
  // is the partner OWNER's id (the account belongs to the owner), so
  // the previous precedence sent the owner's id in both URL segments.
  // The push-subscription endpoint requires `cognito_id` to match the
  // JWT sub or it returns 401. The user's own cognito_id is what the
  // auth flow set on `authentication.user.cognito_id` / `session.user.cognito_id`,
  // so prefer those.
  const cognito_id =
    state?.authentication?.user?.cognito_id ||
    state?.session?.user?.cognito_id ||
    state?.session?.account?.cognito_id;
  if (!account_id || !cognito_id) return null;
  return { account_id, cognito_id };
}

/**
 * Returns true when redux's user record already lists an active
 * push_subscriptions row whose endpoint matches the SW subscription we're
 * about to register. `getAccountUser`
 * (backend/api/client/utilities/account-utils.js:188-202) joins
 * push_subscriptions into the /users/get-user payload — `endpoint` is
 * pulled out of the JSONB subscription column. When the user record
 * hasn't loaded yet (`user === false` on initial mount), we return false
 * so the POST runs and the backend's "already exists" branch handles
 * dedup.
 */
function backendAlreadyHasEndpoint(endpoint: string): boolean {
  const state: any = store.getState();
  const subscriptions = state?.authentication?.user?.push_subscriptions;
  if (!Array.isArray(subscriptions)) return false;
  return subscriptions.some(
    (row: any) => row?.status === 'active' && row?.endpoint === endpoint,
  );
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Returns the existing browser push subscription, or null. Safe to call in any
 * environment — returns null on native and on browsers without push support.
 */
export async function getCurrentWebPushSubscription(): Promise<PushSubscription | null> {
  if (!hasPushSupport()) return null;
  try {
    const reg = await awaitActiveServiceWorker();
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Idempotent: if the user already has an active browser subscription AND the
 * backend has it on file, returns success without doing the round-trip. Use
 * this as the fire-and-forget post-auth bootstrap.
 *
 * Returns `{ success: false, unsupported: true }` on native or when the
 * browser lacks push support — callers can swallow this silently.
 */
export async function subscribeWebPush(): Promise<SubscribeResult> {
  if (!hasPushSupport()) return { success: false, unsupported: true, message: 'Push not supported in this runtime.' };
  if (!config.webPush.publicKey) {
    return { success: false, message: 'VAPID public key missing — set EXPO_PUBLIC_VAPID_PUBLIC_KEY.' };
  }

  const auth = getAuthIdentity();
  if (!auth) return { success: false, message: 'Not authenticated.' };

  // Distinguish "no SW exists on this surface" (Expo web dev server, certain
  // CDN/preview hosts that don't ship the SW asset) from "SW exists but isn't
  // active yet" (slow install, sw.js fetch failed). The former is a genuine
  // unsupported runtime; the latter is a real failure worth logging.
  if (!(await hasAnyServiceWorkerRegistration())) {
    return { success: false, unsupported: true, message: 'No service worker registered for this origin.' };
  }

  const reg = await awaitActiveServiceWorker();
  if (!reg) return { success: false, message: 'Service worker not ready.' };

  // Permission gate. If already granted we proceed; if denied we exit
  // (we never re-prompt — the browser would silently reject anyway). For the
  // first-time `default` state we ask once.
  if (Notification.permission === 'denied') {
    return { success: false, denied: true, message: 'Notification permission denied.' };
  }
  if (Notification.permission !== 'granted') {
    let result: NotificationPermission;
    try {
      result = await Notification.requestPermission();
    } catch {
      return { success: false, message: 'Permission request failed.' };
    }
    if (result !== 'granted') return { success: false, denied: true, message: 'Notification permission not granted.' };
  }

  // Subscribe to the browser's PushManager. If a subscription already exists
  // we reuse it — re-subscribing with the same applicationServerKey returns
  // the same PushSubscription, but reading-then-subscribing-only-if-missing
  // saves an unnecessary IndexedDB write in the SW.
  const applicationServerKey = vapidKeyToUint8Array(config.webPush.publicKey);
  if (!applicationServerKey) {
    return { success: false, message: 'Invalid VAPID public key.' };
  }

  let subscription: PushSubscription | null = null;
  let preexistingBrowserSub = false;
  try {
    subscription = await reg.pushManager.getSubscription();
    preexistingBrowserSub = !!subscription;
    if (!subscription) {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    }
  } catch (err: any) {
    return { success: false, message: err?.message || 'Push subscribe failed.' };
  }

  // Skip the backend POST when redux's user record already lists this
  // endpoint as an active push_subscriptions row. Without this, App.tsx's
  // post-auth bootstrap re-POSTs on every mount and the backend returns
  // 400 "Subscription already exists" — coerced to success client-side
  // but still logged server-side as noise on every page load.
  //
  // A rotated endpoint (different URL than what's stored) falls through
  // and POSTs again. So does a missing/unloaded user record — we'd rather
  // POST and let the backend dedupe than skip and leave the row uncreated.
  //
  // `alreadyActive` is set when both the browser AND the backend had this
  // endpoint on file before we ran — i.e. this call was a pure no-op. The
  // settings screen uses it to suppress the "Push Notifications Enabled"
  // alert that would otherwise misleadingly imply a fresh enable happened.
  if (backendAlreadyHasEndpoint(subscription.endpoint)) {
    return { success: true, alreadyActive: preexistingBrowserSub };
  }

  // POST the subscription up to the backend. The same controller the legacy
  // web app calls (no backend changes). 400 with "Subscription already
  // exists" is treated as success — the row is already on file and we'd just
  // be re-asserting it.
  // `device` lands as a column value when the backend lambda spreads
  // ...config into a `database.insert("push_subscriptions", ...)`. The
  // device column is JSONB; the postgres driver doesn't auto-stringify
  // raw JS objects for JSONB targets, so passing the object directly
  // produces "invalid input syntax for type json" at the DB layer.
  // Pre-stringify here — safe because postgres accepts JSON-formatted
  // text into JSONB columns. Legacy frontend never sent `device` so
  // this column had no production traffic until the desktop build
  // started writing to it. (subscription is also JSONB but the
  // backend handles it separately; only device needs the explicit
  // stringify on the wire.)
  const payload: { subscription: any; config: PushSubscriptionConfig } = {
    subscription,
    config: {
      device: JSON.stringify(describeDevice()),
      device_id: getOrCreateDeviceId(),
      // time_zone is JSONB. readTimezone() returns a bare IANA string
      // (e.g. "America/New_York") — Postgres's JSONB parser rejects it
      // because a bare unquoted string isn't valid JSON. Stringifying
      // produces the literal `"America/New_York"` (with quotes) which
      // is. Same fix the `device` column needed in PR #148.
      time_zone: JSON.stringify(readTimezone()),
    },
  };

  try {
    const res = await apiPost(
      `/users/create-push-subscription/${auth.account_id}/${auth.cognito_id}`,
      payload,
    );
    if (res?.success) return { success: true };
    if (res?.message && /already exists/i.test(res.message)) return { success: true };
    return { success: false, message: res?.message || 'Could not register subscription.' };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Network error registering subscription.' };
  }
}

/**
 * Browser-side unsubscribe + flag the backend row as inactive. Use when the
 * user revokes notifications from settings, or as part of sign-out cleanup.
 */
export async function unsubscribeWebPush(): Promise<SubscribeResult> {
  if (!hasPushSupport()) return { success: false, unsupported: true };

  const auth = getAuthIdentity();
  const reg = await awaitActiveServiceWorker();
  if (!reg) return { success: false, message: 'Service worker not ready.' };

  let endpoint: string | null = null;
  try {
    const subscription = await reg.pushManager.getSubscription();
    if (subscription) {
      endpoint = subscription.endpoint;
      await subscription.unsubscribe();
    }
  } catch (err: any) {
    return { success: false, message: err?.message || 'Push unsubscribe failed.' };
  }

  // Inform the backend so its push_subscriptions row is marked inactive and
  // we don't try to push to a now-defunct endpoint. Best-effort — if we're
  // unauthenticated (e.g. mid-signout) the backend cleanup will happen the
  // next time we successfully push and get a 410 Gone back.
  if (auth && endpoint) {
    try {
      await apiPost(
        `/users/update-push-subscription/${auth.account_id}/${auth.cognito_id}`,
        { endpoint, updates: { status: 'inactive' } },
      );
    } catch {
      // Non-fatal — browser unsubscribe already succeeded.
    }
  }

  return { success: true };
}
